import React from 'react';
import { Col, Icon, Modal, Popover, Row, Spin, Switch, Tooltip, Typography } from 'antd';

import { Authen } from '@redux/model';
import { PANGO_PERMISSIONS } from 'o2o_layout';
import useGetOrg2FAPolicy from './hooks/useGetOrg2FAPolicy';
import { checkPermission } from '../../../../../common/helper';
import useUpdateOrg2FAPolicy from './hooks/useUpdateOrg2FAPolicy';

interface IProps {
  authen: Authen;
}

const Require2FA = (props: IProps) => {
  const { isLoading: isLoadingGet, response, handleGetOrg2FAPolicy } = useGetOrg2FAPolicy();
  const { isLoading: isLoadingUpdate, response: responseUpdate, handleUpdateOrg2FAPolicy } = useUpdateOrg2FAPolicy();

  const permisionOrganizationManagement: boolean = checkPermission(
    PANGO_PERMISSIONS.permissions.OrganizationManagement.key,
    props.authen.permission
  );

  React.useEffect(() => {
    handleGetOrg2FAPolicy();
  }, []);

  React.useEffect(() => {
    if (responseUpdate?.success) {
      const timer = setTimeout(() => {
        handleGetOrg2FAPolicy();
      }, 1000);
      return () => clearTimeout(timer);
    }
    return;
  }, [responseUpdate]);

  const handleToggle = () => {
    if (!permisionOrganizationManagement) return;

    const isCurrentlyEnabled = response?.result?.restrict2FA;

    Modal.confirm({
      title: `${isCurrentlyEnabled ? 'Disable' : 'Enable'} Two-Factor Authentication (2FA)`,
      content: isCurrentlyEnabled
        ? 'Disabling this will allow users to access the organization without 2FA, which may increase the risk of unauthorized access.'
        : 'When enabled, all users must set up 2FA before accessing the organization. This adds an extra layer of security and helps prevent unauthorized access.',
      okText: isCurrentlyEnabled ? 'Disable' : 'Enable',
      okType: isCurrentlyEnabled ? 'danger' : 'primary',
      cancelText: 'Cancel',
      onOk: () => {
        handleUpdateOrg2FAPolicy({
          restrict2FA: !isCurrentlyEnabled,
        });
      },
    });
  };

  return (
    <Spin spinning={isLoadingGet}>
      <Row gutter={[10, 10]} type='flex'>
        <Col xs={24}>
          <Typography.Title level={4}>Two-Factor Authentication (2FA)</Typography.Title>
          <Typography.Text type='secondary'>
            Require users to enable Two-Factor Authentication (2FA) before accessing the organization to add an extra
            layer of security and prevent unauthorized access.
          </Typography.Text>
        </Col>
        <Col xs={24}>
          <Tooltip title={!permisionOrganizationManagement && 'Missing Permission: Organization Management'}>
            <Switch
              loading={isLoadingUpdate}
              checkedChildren={'Enable'}
              unCheckedChildren={'Disable'}
              disabled={!permisionOrganizationManagement}
              checked={response?.result?.restrict2FA}
              style={{ marginRight: 20 }}
              onClick={handleToggle}
            />
          </Tooltip>
          Require Two-Factor Authentication
          <Popover
            overlayStyle={{
              maxWidth: 500,
            }}
            content={
              <>
                <strong>Enable:</strong> When enabled, all users must set up 2FA before accessing the organization. This
                adds an extra layer of security and helps prevent unauthorized access.
                <br />
                <strong>Disable:</strong> Disabling this will allow users to access the organization without 2FA, which
                may increase the risk of unauthorized access.
              </>
            }
          >
            <Icon style={{ marginLeft: 5 }} type='question-circle' />
          </Popover>
        </Col>
      </Row>
    </Spin>
  );
};

export default Require2FA;
